/**
 * API Client Utility
 * 
 * Centralized API client for all backend requests with:
 * - Automatic cookie-based authentication
 * - Error handling
 * - Token refresh on 401
 * - Typed responses
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class APIClient {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    /**
     * Make an authenticated API request
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        
        const config = {
            ...options,
            credentials: 'include', // Important: include cookies
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };

        try {
            let response = await fetch(url, config);

            // If 401 Unauthorized, try to refresh token then retry
            if (response.status === 401) {
                const refreshed = await this.refreshToken();
                if (refreshed) {
                    response = await fetch(url, config);
                }
                if (!response.ok) {
                    // Refresh failed or retry still 401 - redirect to login
                    if (!refreshed) {
                        console.log('[APIClient] Token refresh failed - redirecting to login');
                    }
                    this.handleAuthFailure();
                    throw new Error('Authentication failed');
                }
            }

            // If 403 Forbidden, likely authentication issue
            if (response.status === 403) {
                const error = await response.json().catch(() => ({}));
                if (error.detail && (error.detail.includes('credentials') || error.detail.includes('authenticated'))) {
                    console.log('[APIClient] Authentication error (403) - redirecting to login');
                    this.handleAuthFailure();
                    throw new Error('Authentication failed');
                }
            }

            // Handle non-2xx responses
            if (!response.ok) {
                const error = await response.json().catch(() => ({
                    detail: `HTTP ${response.status}: ${response.statusText}`
                }));
                
                // Check if error message indicates auth failure
                const errorMsg = error.detail || error.message || '';
                if (errorMsg.includes('Could not validate credentials') || 
                    errorMsg.includes('Not authenticated') ||
                    errorMsg.includes('Invalid token')) {
                    console.log('[APIClient] Authentication error detected - redirecting to login');
                    this.handleAuthFailure();
                    throw new Error('Authentication failed');
                }
                
                throw new Error(errorMsg || 'Request failed');
            }

            // Handle 204 No Content
            if (response.status === 204) {
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`API request failed: ${endpoint}`, error);
            throw error;
        }
    }

    /**
     * Refresh access token using refresh token cookie
     */
    async refreshToken() {
        try {
            const response = await fetch(`${this.baseURL}/v1/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
            });

            if (response.ok) {
                console.log('Token refreshed successfully');
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Token refresh failed:', error);
            return false;
        }
    }

    /**
     * Handle authentication failure
     */
    handleAuthFailure() {
        // Clear local storage
        localStorage.removeItem('currentUser');
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        
        // Redirect to login
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }

    // ============================================
    // Authentication Endpoints
    // ============================================

    /**
     * Get current user information
     */
    async getCurrentUser() {
        return this.request('/v1/auth/me');
    }

    /**
     * Logout user
     */
    async logout() {
        return this.request('/v1/auth/logout', { method: 'POST' });
    }

    // ============================================
    // Family Endpoints
    // ============================================

    /**
     * Get all families user has access to
     */
    async getFamilies() {
        return this.request('/v1/families');
    }

    /**
     * Get a specific family
     */
    async getFamily(familyId) {
        return this.request(`/v1/families/${familyId}`);
    }

    /**
     * Create a new family
     */
    async createFamily(familyData) {
        return this.request('/v1/families', {
            method: 'POST',
            body: JSON.stringify(familyData),
        });
    }

    /**
     * Update a family
     */
    async updateFamily(familyId, familyData) {
        return this.request(`/v1/families/${familyId}`, {
            method: 'PUT',
            body: JSON.stringify(familyData),
        });
    }

    /**
     * Delete a family
     */
    async deleteFamily(familyId) {
        return this.request(`/v1/families/${familyId}`, {
            method: 'DELETE',
        });
    }

    /**
     * Get family members
     */
    async getFamilyMembers(familyId) {
        return this.request(`/v1/families/${familyId}/members`);
    }

    /**
     * Grant family permission to a user
     */
    async grantFamilyPermission(familyId, userEmail, accessLevel) {
        return this.request(`/v1/families/${familyId}/permissions`, {
            method: 'POST',
            body: JSON.stringify({
                user_email: userEmail,
                access: accessLevel,
            }),
        });
    }

    /**
     * Revoke family permission from a user
     */
    async revokeFamilyPermission(familyId, userId) {
        return this.request(`/v1/families/${familyId}/permissions/${userId}`, {
            method: 'DELETE',
        });
    }

    // ============================================
    // Category Endpoints
    // ============================================

    /**
     * Get all categories for a family
     */
    async getCategories(familyId) {
        return this.request(`/v1/categories?family_id=${familyId}`);
    }

    /**
     * Update category (enable/disable)
     */
    async updateCategory(categoryId, categoryData) {
        return this.request(`/v1/categories/${categoryId}`, {
            method: 'PUT',
            body: JSON.stringify(categoryData),
        });
    }

    // ============================================
    // Item Endpoints (Generic for all categories)
    // ============================================

    /**
     * Get all items in a category
     */
    async getItems(familyId, categorySlug, { skip = 0, limit = 100 } = {}) {
        return this.request(
            `/v1/items/${familyId}/${categorySlug}?skip=${skip}&limit=${limit}`
        );
    }

    /**
     * Get a specific item
     */
    async getItem(familyId, categorySlug, itemId) {
        return this.request(`/v1/items/${familyId}/${categorySlug}/${itemId}`);
    }

    /**
     * Create a new item
     */
    async createItem(familyId, categorySlug, itemData) {
        return this.request(`/v1/items/${familyId}/${categorySlug}`, {
            method: 'POST',
            body: JSON.stringify({ data: itemData }),
        });
    }

    /**
     * Update an item
     */
    async updateItem(familyId, categorySlug, itemId, itemData) {
        return this.request(`/v1/items/${familyId}/${categorySlug}/${itemId}`, {
            method: 'PUT',
            body: JSON.stringify({ data: itemData }),
        });
    }

    /**
     * Delete an item
     */
    async deleteItem(familyId, categorySlug, itemId) {
        return this.request(`/v1/items/${familyId}/${categorySlug}/${itemId}`, {
            method: 'DELETE',
        });
    }

    /**
     * Share an item with another user
     */
    async shareItem(familyId, categorySlug, itemId, userEmail, accessLevel) {
        return this.request(`/v1/items/${familyId}/${categorySlug}/${itemId}/share`, {
            method: 'POST',
            body: JSON.stringify({
                user_email: userEmail,
                access_level: accessLevel,
            }),
        });
    }

    /**
     * Revoke item sharing
     */
    async revokeItemShare(familyId, categorySlug, itemId, userId) {
        return this.request(
            `/v1/items/${familyId}/${categorySlug}/${itemId}/share/${userId}`,
            { method: 'DELETE' }
        );
    }

    // ============================================
    // Permission Endpoints
    // ============================================

    /**
     * Check if user has access to a resource
     */
    async checkAccess(resourceType, resourceId, action) {
        return this.request('/v1/permissions/check', {
            method: 'POST',
            body: JSON.stringify({
                resource_type: resourceType,
                resource_id: resourceId,
                action: action,
            }),
        });
    }

    /**
     * Get all resources shared with the current user
     */
    async getSharedWithMe() {
        return this.request('/v1/permissions/shared-with-me');
    }
}

// Export singleton instance
export const apiClient = new APIClient();

// Export convenience functions
export const api = {
    // Auth
    getCurrentUser: () => apiClient.getCurrentUser(),
    logout: () => apiClient.logout(),
    
    // Families
    getFamilies: () => apiClient.getFamilies(),
    getFamily: (id) => apiClient.getFamily(id),
    createFamily: (data) => apiClient.createFamily(data),
    updateFamily: (id, data) => apiClient.updateFamily(id, data),
    deleteFamily: (id) => apiClient.deleteFamily(id),
    getFamilyMembers: (id) => apiClient.getFamilyMembers(id),
    grantFamilyPermission: (id, email, access) => 
        apiClient.grantFamilyPermission(id, email, access),
    revokeFamilyPermission: (id, userId) => 
        apiClient.revokeFamilyPermission(id, userId),
    
    // Categories
    getCategories: (familyId) => apiClient.getCategories(familyId),
    updateCategory: (id, data) => apiClient.updateCategory(id, data),
    
    // Items
    getItems: (familyId, category, opts) => 
        apiClient.getItems(familyId, category, opts),
    getItem: (familyId, category, itemId) => 
        apiClient.getItem(familyId, category, itemId),
    createItem: (familyId, category, data) => 
        apiClient.createItem(familyId, category, data),
    updateItem: (familyId, category, itemId, data) => 
        apiClient.updateItem(familyId, category, itemId, data),
    deleteItem: (familyId, category, itemId) => 
        apiClient.deleteItem(familyId, category, itemId),
    shareItem: (familyId, category, itemId, email, access) => 
        apiClient.shareItem(familyId, category, itemId, email, access),
    revokeItemShare: (familyId, category, itemId, userId) => 
        apiClient.revokeItemShare(familyId, category, itemId, userId),
    
    // Permissions
    checkAccess: (type, id, action) => apiClient.checkAccess(type, id, action),
    getSharedWithMe: () => apiClient.getSharedWithMe(),
};

export default apiClient;

